import { useRef, useEffect, useMemo } from 'react'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import { useTheme, alpha } from '@mui/material/styles'
import { PERSONAS, STACK_ORDER } from './cohorts'

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
  CanvasRenderer,
])

const HEIGHT = 320

// Sums the raw cohort series into one line per persona, in stack order
// (bottom band first). The persona partition is exclusive and
// exhaustive, so the lines always add up to the total mic-on count at
// each timestamp.
const toPersonaSeries = (series) =>
  STACK_ORDER.map((persona) => {
    const byTimestamp = new Map()
    series.forEach((entry) => {
      if (!persona.match(entry.labels || {})) return
      entry.points.forEach(([ts, value]) => {
        byTimestamp.set(ts, (byTimestamp.get(ts) || 0) + value)
      })
    })
    return {
      ...persona,
      data: [...byTimestamp.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([ts, value]) => [ts * 1000, value]),
    }
  })

// One tooltip/axis-pointer position shared by every chart in the group
// (this chart and the cumulative one below it).
export const CHART_GROUP = 'heartbeats-history'

// Series render in stack order; the tooltip lists them in legend order.
const LEGEND_RANK = new Map(PERSONAS.map(({ key }, index) => [key, index]))

const tooltipFormatter = (params) => {
  const rows = [...params]
    .sort(
      (a, b) => LEGEND_RANK.get(a.seriesName) - LEGEND_RANK.get(b.seriesName)
    )
    .map(
      ({ marker, seriesName, value }) =>
        `<div style="display:flex;justify-content:space-between;gap:18px;">
          <span>${marker}${seriesName}</span><b>${Math.round(value[1])}</b>
        </div>`
    )
    .join('')
  const total = params.reduce((sum, { value }) => sum + value[1], 0)
  return `<div style="min-width:140px">
    <div style="margin-bottom:4px;">${params[0].axisValueLabel}</div>
    ${rows}
    <div style="display:flex;justify-content:space-between;gap:18px;
      border-top:1px solid rgba(255,255,255,0.25);margin-top:4px;padding-top:4px;">
      <span>Total</span><b>${Math.round(total)}</b>
    </div>
  </div>`
}

// Grafana-style time series panel: personas as a stacked area (the top
// edge is the total online), shared crosshair tooltip, click-to-focus
// legend, and drag-select zoom (which reports the selection upward so
// the parent refetches the window at finer resolution — the chart never
// zooms locally).
const Timeline = ({ series, rangeStart, rangeEnd, onZoom }) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const onZoomRef = useRef(onZoom)
  onZoomRef.current = onZoom
  const theme = useTheme()

  const personaSeries = useMemo(() => toPersonaSeries(series || []), [series])

  useEffect(() => {
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart
    chart.group = CHART_GROUP
    echarts.connect(CHART_GROUP)

    // Drag on the plot = select a window (ECharts "dataZoomSelect").
    chart.on('datazoom', (event) => {
      const zoom = event.batch?.[0] || event
      if (zoom.startValue == null || zoom.endValue == null) return
      onZoomRef.current?.(zoom.startValue / 1000, zoom.endValue / 1000)
      // The select gesture also zoomed this chart locally, and that
      // window (absolute timestamps) would keep capping the axis after
      // the range picker moves on. Undo it immediately — the visible
      // zoom comes from the refetch with new axis bounds. (The reset
      // dispatch re-fires this handler percent-based, so the startValue
      // guard above stops recursion.)
      chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 })
    })

    // Legend click focuses that one group — off the stack, so the chart
    // shows just its own counts from a zero baseline. Clicking the
    // focused group again restores everyone. (Overrides the default
    // per-series hide/show toggle, which ECharts has already applied by
    // the time this fires; `selected` is the post-toggle state.)
    chart.on('legendselectchanged', ({ name, selected }) => {
      const names = Object.keys(selected)
      const before = { ...selected, [name]: !selected[name] }
      const wasFocused =
        before[name] && names.every((n) => n === name || !before[n])
      const target = {}
      names.forEach((n) => {
        target[n] = wasFocused ? true : n === name
      })
      chart.setOption({ legend: { selected: target } })
      // setOption drops the armed drag-select cursor — re-arm it.
      chart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      })
    })

    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const textColor = theme.palette.text.secondary
    const gridColor = alpha(theme.palette.text.secondary, 0.15)

    chart.setOption(
      {
        animationDuration: 900,
        animationDurationUpdate: 500,
        legend: {
          top: 0,
          icon: 'circle',
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 16,
          textStyle: { color: theme.palette.text.primary },
          inactiveColor: alpha(theme.palette.text.secondary, 0.3),
          data: PERSONAS.map(({ key }) => key),
        },
        grid: { left: 44, right: 16, top: 36, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          backgroundColor: 'rgba(33, 33, 41, 0.94)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: tooltipFormatter,
          // Pin the tooltip to the crosshair's x (the snapped data
          // point), not the mouse — past the last point the crosshair
          // stops, and the tooltip stays with it.
          position: (point, params, dom, rect, size) => {
            const items = Array.isArray(params) ? params : [params]
            const x = chart.convertToPixel(
              { xAxisIndex: 0 },
              items[0].axisValue
            )
            const [w, h] = size.contentSize
            const left = x + 14 + w > size.viewSize[0] ? x - 14 - w : x + 14
            const top = Math.min(
              Math.max(point[1] - h / 2, 8),
              size.viewSize[1] - h - 8
            )
            return [left, top]
          },
        },
        // The toolbox must actually render for its dataZoom feature (the
        // drag-select machinery) to exist — `show: false` would skip
        // creating it entirely. Blank icon paths keep it invisible.
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none',
              icon: { zoom: 'path://', back: 'path://' },
            },
          },
        },
        // No inside dataZoom: it would capture wheel events (blocking
        // page scroll) and isn't needed — the toolbox drag-select
        // dispatches its window upward and zooming happens by refetch.
        xAxis: {
          type: 'time',
          min: rangeStart * 1000,
          max: rangeEnd * 1000,
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: gridColor } },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          min: 0,
          minInterval: 1,
          axisLabel: { color: textColor },
          splitLine: { lineStyle: { color: gridColor } },
        },
        series: personaSeries.map(({ key, color, data }) => ({
          name: key,
          type: 'line',
          stack: 'online',
          data,
          color,
          showSymbol: false,
          lineStyle: { width: 1 },
          emphasis: { focus: 'series' },
          areaStyle: { opacity: 0.5 },
        })),
      }
      // Merged (not replaced): the four persona series are fixed in name
      // and order, so refreshes morph the lines in place instead of
      // clearing and re-entering the whole stack.
    )

    // Re-arm select-zoom after every option update.
    chart.dispatchAction({
      type: 'takeGlobalCursor',
      key: 'dataZoomSelect',
      dataZoomSelectActive: true,
    })
  }, [personaSeries, rangeStart, rangeEnd, theme])

  return <Box ref={containerRef} sx={{ width: '100%', height: HEIGHT }} />
}

export default Timeline
